import * as Apollo from '@apollo/client';
import type { ApolloClient } from '@apollo/client';
import { DataProductApi, DependencyApi } from '../constants/api';
import * as React from 'react';
import { RETRY_UNAUTHORIZED } from '../utils/unauthorizedRetryLink';

// Re-export everything from @apollo/client so codegen imports work
export * from '@apollo/client';

interface ApiContext {
  apiName?: DataProductApi | DependencyApi;
}

export type GsocApolloClients = Record<DataProductApi, ApolloClient<object>>;

export const GsocApolloContext = React.createContext<GsocApolloClients | null>(null);

export type DependencyApiApolloClients = Record<DependencyApi, ApolloClient<object>>;

export const DependencyApiApolloContext = React.createContext<DependencyApiApolloClients | null>(null);

/**
 * Resolves the client option. Returns undefined if no GsocApolloProvider is present,
 * which lets Apollo fall back to the nearest ApolloProvider/MockedProvider (useful in tests).
 */
function useResolvedClient(context: unknown): Apollo.ApolloClient<object> | undefined {
  const apiName = (context as ApiContext | undefined)?.apiName;

  const gsocClients = React.useContext(GsocApolloContext);
  const externalClients = React.useContext(DependencyApiApolloContext);
  const clients = React.useMemo(() => ({ ...gsocClients, ...externalClients }), [gsocClients, externalClients]);

  switch (apiName) {
    case DataProductApi.CONTROL_PLANE_DATA_PRODUCT_API:
    case DataProductApi.DATA_PLANE_API:
    case DataProductApi.CURATED_API:
    case DataProductApi.ANTENNA_DATA_PRODUCT_API:
    case DependencyApi.AMAZON_APPROVALS_API:
      return clients[apiName];
    default:
      return clients[DataProductApi.CURATED_API];
  }
}

export function useDataProductClient(api?: DataProductApi): ApolloClient<object> | undefined {
  const clients = React.useContext(GsocApolloContext);
  if (!clients) return undefined;
  return clients[api ?? DataProductApi.CURATED_API];
}

// Override hooks to resolve the correct client from context based on apiName

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useQuery<TData = any, TVariables extends Apollo.OperationVariables = Apollo.OperationVariables>(
  query: Apollo.DocumentNode | Apollo.TypedDocumentNode<TData, TVariables>,
  options?: Apollo.QueryHookOptions<TData, TVariables>,
): Apollo.QueryResult<TData, TVariables> {
  const client = useResolvedClient(options?.context);
  return Apollo.useQuery<TData, TVariables>(query, { ...options, ...(client && { client }) });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useLazyQuery<TData = any, TVariables extends Apollo.OperationVariables = Apollo.OperationVariables>(
  query: Apollo.DocumentNode | Apollo.TypedDocumentNode<TData, TVariables>,
  options?: Apollo.LazyQueryHookOptions<TData, TVariables>,
): Apollo.LazyQueryResultTuple<TData, TVariables> {
  const client = useResolvedClient(options?.context);
  const { context: _, ...restOptions } = options ?? {};
  return Apollo.useLazyQuery<TData, TVariables>(query, {
    ...restOptions,
    ...(client && { client }),
  } as Apollo.LazyQueryHookOptions<TData, TVariables>);
}

export function useSuspenseQuery<
  TData = unknown,
  TVariables extends Apollo.OperationVariables = Apollo.OperationVariables,
>(
  query: Apollo.DocumentNode | Apollo.TypedDocumentNode<TData, TVariables>,
  options?: Apollo.SuspenseQueryHookOptions<TData, TVariables> | Apollo.SkipToken,
) {
  const context = options && typeof options !== 'symbol' ? options.context : undefined;
  const client = useResolvedClient(context);
  // When SkipToken is passed, we still need to call the hook unconditionally
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const resolvedOptions = options && typeof options !== 'symbol' ? { ...options, ...(client && { client }) } : options!;
  return Apollo.useSuspenseQuery<TData, TVariables>(query, resolvedOptions);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useMutation<TData = any, TVariables = Apollo.OperationVariables>(
  mutation: Apollo.DocumentNode | Apollo.TypedDocumentNode<TData, TVariables>,
  options?: Apollo.MutationHookOptions<TData, TVariables>,
): Apollo.MutationTuple<TData, TVariables> {
  const client = useResolvedClient(options?.context);
  return Apollo.useMutation<TData, TVariables>(mutation, { ...options, ...(client && { client }) });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useSubscription<TData = any, TVariables extends Apollo.OperationVariables = Apollo.OperationVariables>(
  subscription: Apollo.DocumentNode | Apollo.TypedDocumentNode<TData, TVariables>,
  options?: Apollo.SubscriptionHookOptions<TData, TVariables>,
): Apollo.SubscriptionResult<TData, TVariables> {
  const client = useResolvedClient(options?.context);
  return Apollo.useSubscription<TData, TVariables>(subscription, { ...options, ...(client && { client }) });
}

// --- useQueries: TanStack-style parallel queries for dynamic lists ---
// Reference: https://tanstack.com/query/v4/docs/framework/react/reference/useQueries

export interface UseQueriesQueryConfig<TData, TVariables extends Apollo.OperationVariables> {
  readonly query: Apollo.DocumentNode | Apollo.TypedDocumentNode<TData, TVariables>;
  readonly variables: TVariables;
}

export interface UseQueriesQueryResult<TData> {
  readonly data: TData | undefined;
  readonly error: unknown;
  /** Errors returned alongside data by a client with errorPolicy 'all'; the query still counts as a success. */
  readonly graphQLErrors?: Apollo.ApolloQueryResult<TData>['errors'];
  readonly isLoading: boolean;
  readonly isSuccess: boolean;
  readonly isError: boolean;
}

/**
 * Executes a dynamic list of queries in parallel using the imperative client.query() API.
 * Modeled after TanStack Query's useQueries — returns one result object per input query,
 * each with its own data/error/loading state.
 *
 * Necessary when the number of queries is not known statically and hooks cannot be called in a loop.
 * Re-fetches whenever the queries' variables change structurally.
 *
 * The hook is safe to call with a non-memoized input (it uses a serialized key internally to
 * avoid infinite re-fetches), but callers should still memoize the `queries` array for
 * performance — avoids redundant serialization and object allocation on every render.
 */
export function useQueries<TData, TVariables extends Apollo.OperationVariables>(
  api: DataProductApi,
  queries: readonly UseQueriesQueryConfig<TData, TVariables>[],
  options: {
    /**
     * Retry a 401 through `unauthorizedRetryLink`. For callers that fire a parallel burst, which the authorizer can
     * throttle; a genuine denial then takes up to about 3.5 s longer to surface.
     */
    readonly retryUnauthorized?: boolean;
  } = {},
): UseQueriesQueryResult<TData>[] {
  const { retryUnauthorized = false } = options;
  const client = useDataProductClient(api);
  const [state, setState] = React.useState<{ key: string; results: UseQueriesQueryResult<TData>[] }>({
    key: '',
    results: [],
  });

  // Serialize query variables to a stable string key so the effect only re-runs when the
  // queries change structurally — not on every render due to a new array reference.
  // This matches TanStack useQueries behavior where callers don't need to memoize input.
  const queriesKey = JSON.stringify(queries.map(q => q.variables));
  const setResults = React.useCallback(
    (results: UseQueriesQueryResult<TData>[]) => setState({ key: queriesKey, results }),
    [queriesKey],
  );

  // Store queries in a ref so the effect can access the current value without
  // adding it as a dependency (which would defeat the queriesKey approach).
  const queriesRef = React.useRef(queries);
  queriesRef.current = queries;

  React.useEffect(() => {
    const currentQueries = queriesRef.current;

    if (client == null || currentQueries.length === 0) {
      setResults([]);
      return;
    }

    setResults(
      currentQueries.map(() => ({
        data: undefined,
        error: undefined,
        isLoading: true,
        isSuccess: false,
        isError: false,
      })),
    );

    let cancelled = false;

    const promises = currentQueries.map(q =>
      client
        .query<TData, TVariables>({
          query: q.query,
          variables: q.variables,
          returnPartialData: false,
          context: { [RETRY_UNAUTHORIZED]: retryUnauthorized },
        })
        .then(result => ({
          data: result.data,
          error: undefined,
          graphQLErrors: result.errors,
          isLoading: false,
          isSuccess: true,
          isError: false,
        }))
        .catch((error: unknown) => ({ data: undefined, error, isLoading: false, isSuccess: false, isError: true })),
    );

    void Promise.all(promises).then(items => {
      if (cancelled) return;
      setResults(items);
    });

    return () => {
      cancelled = true;
    };
  }, [client, queriesKey, retryUnauthorized, setResults]);

  // Until the effect catches up with new queries, the stored results belong to the old ones, so report loading
  // rather than let callers that match results to queries by position pair them with the wrong query.
  const queryCount = queries.length;
  const hasClient = client != null;
  const pending = React.useMemo(
    () => (hasClient ? Array.from({ length: queryCount }, () => LOADING_RESULT) : []),
    [hasClient, queryCount],
  );
  return state.key === queriesKey ? state.results : pending;
}

const LOADING_RESULT: UseQueriesQueryResult<never> = {
  data: undefined,
  error: undefined,
  isLoading: true,
  isSuccess: false,
  isError: false,
};
